import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Plus, X, Loader2, Briefcase } from 'lucide-react';

export default function PostJobPage() {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const isEdit = !!jobId;
  const { api, user } = useAuth();
  const featuredCredits = user?.service_credits?.featured_job_post || 0;
  const urgentCredits = user?.service_credits?.job_advert_credit || 0;

  const [loading, setLoading] = useState(false);
  const [jobLoading, setJobLoading] = useState(isEdit);
  // When editing, keep the saved location until the employer changes a location field.
  const locationEdited = useRef(!isEdit);
  const prefillSubcategory = useRef('');
  const savedGeo = useRef({ country: undefined, city: undefined });
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    company_name: '',
    location: '',
    salary_min: '',
    salary_max: '',
    currency: 'GBP',
    salary_period: 'year',
    job_type: 'full_time',
    remote: false,
    is_featured: false,
    is_urgent: false,
    experience_required: '',
    industry: '',
    category: '',
    subcategory: ''
  });
  
  const [requirements, setRequirements] = useState([]);
  const [benefits, setBenefits] = useState([]);
  const [skills, setSkills] = useState([]);
  const [newRequirement, setNewRequirement] = useState('');
  const [newBenefit, setNewBenefit] = useState('');
  const [newSkill, setNewSkill] = useState('');

  // Location: Country -> Region -> City, falling back to manual text entry
  // when a country has no regions/cities set up yet in the admin.
  const [jobCountries, setJobCountries] = useState([]);
  const [jobRegions, setJobRegions] = useState([]);
  const [jobCities, setJobCities] = useState([]);
  const [selectedCountryCode, setSelectedCountryCode] = useState('');
  const [selectedRegionId, setSelectedRegionId] = useState('');
  const [selectedCityName, setSelectedCityName] = useState('');
  const [manualRegion, setManualRegion] = useState('');
  const [manualCity, setManualCity] = useState('');

  // Category -> Subcategory and Currency
  const [jobCategories, setJobCategories] = useState([]);
  const [jobSubcategories, setJobSubcategories] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  useEffect(() => {
    api.get('/countries')
      .then(res => setJobCountries(res.data.countries || []))
      .catch(() => setJobCountries([]));
    api.get('/jobs/categories')
      .then(res => setJobCategories(res.data.categories || []))
      .catch(() => setJobCategories([]));
    api.get('/currencies')
      .then(res => setCurrencies(res.data.currencies || []))
      .catch(() => setCurrencies([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/jobs/${jobId}`)
      .then(res => {
        const j = res.data;
        savedGeo.current = { country: j.country || undefined, city: j.city || undefined };
        prefillSubcategory.current = j.subcategory || '';
        setFormData(prev => ({
          ...prev,
          title: j.title || '',
          description: j.description || '',
          company_name: j.company_name || '',
          location: j.location || '',
          salary_min: j.salary_min ?? '',
          salary_max: j.salary_max ?? '',
          currency: j.currency || 'GBP',
          salary_period: j.salary_period || 'year',
          job_type: j.job_type || 'full_time',
          remote: !!j.remote,
          is_featured: !!j.is_featured,
          is_urgent: !!j.is_urgent,
          experience_required: j.experience_required || '',
          industry: j.industry || '',
          category: j.category || '',
          subcategory: j.subcategory || ''
        }));
        setRequirements(j.requirements || []);
        setBenefits(j.benefits || []);
        setSkills(j.skills_required || []);
        if (j.country) setSelectedCountryCode(j.country);
      })
      .catch(() => {
        toast.error('Could not load this job');
        navigate('/employer/dashboard');
      })
      .finally(() => setJobLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  useEffect(() => {
    setFormData(prev => ({ ...prev, subcategory: prefillSubcategory.current }));
    if (!formData.category) { setJobSubcategories([]); return; }
    const cat = jobCategories.find(c => (c.slug || c.name) === formData.category);
    if (!cat) { setJobSubcategories([]); return; }
    api.get(`/jobs/subcategories?category_id=${cat.id}`)
      .then(res => setJobSubcategories(res.data.subcategories || []))
      .catch(() => setJobSubcategories([]))
      .finally(() => { prefillSubcategory.current = ''; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.category, jobCategories]);

  useEffect(() => {
    setSelectedRegionId('');
    setManualRegion('');
    setJobCities([]);
    setSelectedCityName('');
    setManualCity('');
    if (!selectedCountryCode) { setJobRegions([]); return; }
    api.get(`/regions?country_code=${encodeURIComponent(selectedCountryCode)}`)
      .then(res => setJobRegions(res.data.regions || []))
      .catch(() => setJobRegions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountryCode]);

  useEffect(() => {
    setJobCities([]);
    setSelectedCityName('');
    setManualCity('');
    if (!selectedRegionId) return;
    api.get(`/cities?region_id=${encodeURIComponent(selectedRegionId)}`)
      .then(res => setJobCities(res.data.cities || []))
      .catch(() => setJobCities([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegionId]);

  useEffect(() => {
    if (!locationEdited.current) return;
    const countryName = jobCountries.find(c => c.code === selectedCountryCode)?.name || '';
    const regionName = jobRegions.find(r => r.id === selectedRegionId)?.name || manualRegion;
    const cityName = selectedCityName || manualCity;
    const location = [cityName, regionName, countryName].filter(Boolean).join(', ');
    setFormData(prev => ({ ...prev, location }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountryCode, selectedRegionId, selectedCityName, manualRegion, manualCity, jobCountries, jobRegions]);

  const editLocation = (setter) => (value) => {
    locationEdited.current = true;
    setter(value);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const addItem = (value, list, setList, setNew) => {
    if (value.trim() && !list.includes(value.trim())) {
      setList([...list, value.trim()]);
      setNew('');
    }
  };

  const removeItem = (index, list, setList) => {
    setList(list.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description || !formData.company_name || !formData.location) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const jobData = {
        ...formData,
        country: locationEdited.current ? (selectedCountryCode || undefined) : savedGeo.current.country,
        city: locationEdited.current ? ((selectedCityName || manualCity || '').trim() || undefined) : savedGeo.current.city,
        salary_min: formData.salary_min ? parseInt(formData.salary_min) : null,
        salary_max: formData.salary_max ? parseInt(formData.salary_max) : null,
        requirements,
        benefits,
        skills_required: skills
      };

      if (isEdit) {
        await api.put(`/jobs/${jobId}`, jobData);
        toast.success('Job updated successfully!');
      } else {
        await api.post('/jobs', jobData);
        toast.success('Job posted successfully!');
      }
      navigate('/employer/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || (isEdit ? 'Failed to update job' : 'Failed to post job');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (jobLoading) {
    return (
      <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#003366]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F4F8]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to dashboard
        </button>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#003366]/10 flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-[#003366]" />
              </div>
              <div>
                <CardTitle className="text-2xl">{isEdit ? 'Edit Job' : 'Post a New Job'}</CardTitle>
                <CardDescription>{isEdit ? 'Update the details of this job post' : 'Fill in the details to attract the best candidates'}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Job Title *</Label>
                    <Input
                      id="title"
                      name="title"
                      placeholder="e.g. Senior Software Engineer"
                      value={formData.title}
                      onChange={handleChange}
                      required
                      data-testid="job-title-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company_name">Company Name *</Label>
                    <Input
                      id="company_name"
                      name="company_name"
                      placeholder="Your company name"
                      value={formData.company_name}
                      onChange={handleChange}
                      required
                      data-testid="company-name-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Location *</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <Select value={selectedCountryCode} onValueChange={editLocation(setSelectedCountryCode)}>
                      <SelectTrigger data-testid="location-country-select">
                        <SelectValue placeholder="Country" />
                      </SelectTrigger>
                      <SelectContent>
                        {jobCountries.map(c => (
                          <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {selectedCountryCode && jobRegions.length > 0 ? (
                      <Select value={selectedRegionId} onValueChange={editLocation(setSelectedRegionId)}>
                        <SelectTrigger data-testid="location-region-select">
                          <SelectValue placeholder="Region" />
                        </SelectTrigger>
                        <SelectContent>
                          {jobRegions.map(r => (
                            <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        placeholder="Region (type manually)"
                        value={manualRegion}
                        onChange={e => editLocation(setManualRegion)(e.target.value)}
                        disabled={!selectedCountryCode}
                        data-testid="location-region-input"
                      />
                    )}

                    {selectedRegionId && jobCities.length > 0 ? (
                      <Select value={selectedCityName} onValueChange={editLocation(setSelectedCityName)}>
                        <SelectTrigger data-testid="location-city-select">
                          <SelectValue placeholder="City" />
                        </SelectTrigger>
                        <SelectContent>
                          {jobCities.map(city => (
                            <SelectItem key={city.id} value={city.name}>{city.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        placeholder="City/Town (type manually)"
                        value={manualCity}
                        onChange={e => editLocation(setManualCity)(e.target.value)}
                        disabled={!selectedCountryCode}
                        data-testid="location-city-input"
                      />
                    )}
                  </div>
                  {formData.location && (
                    <p className="text-xs text-muted-foreground">Will be shown as: <span className="font-medium">{formData.location}</span></p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="job_type">Job Type *</Label>
                  <Select
                    value={formData.job_type}
                    onValueChange={(value) => setFormData({ ...formData, job_type: value })}
                  >
                    <SelectTrigger data-testid="job-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full_time">Full Time</SelectItem>
                      <SelectItem value="part_time">Part Time</SelectItem>
                      <SelectItem value="contract">Contract</SelectItem>
                      <SelectItem value="internship">Internship</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {jobCategories.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                        <SelectTrigger data-testid="job-category-select">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {jobCategories.map(cat => (
                            <SelectItem key={cat.id} value={cat.slug || cat.name}>{cat.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Subcategory</Label>
                      <Select
                        value={formData.subcategory}
                        onValueChange={(value) => setFormData({ ...formData, subcategory: value })}
                        disabled={jobSubcategories.length === 0}
                      >
                        <SelectTrigger data-testid="job-subcategory-select">
                          <SelectValue placeholder={jobSubcategories.length === 0 ? 'No subcategories' : 'Select subcategory'} />
                        </SelectTrigger>
                        <SelectContent>
                          {jobSubcategories.map(sub => (
                            <SelectItem key={sub.id} value={sub.slug || sub.name}>{sub.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <Switch
                    id="remote"
                    checked={formData.remote}
                    onCheckedChange={(checked) => setFormData({ ...formData, remote: checked })}
                    data-testid="remote-switch"
                  />
                  <Label htmlFor="remote">This is a remote position</Label>
                </div>
              </div>

              {/* Boosts */}
              <div className="space-y-3 p-4 rounded-lg border bg-secondary/30">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="is_featured">Feature this job</Label>
                    <p className="text-xs text-muted-foreground">Top of search results for 30 days. {featuredCredits > 0 ? `${featuredCredits} credit(s) available.` : 'Requires a Featured Job Posting credit.'}</p>
                  </div>
                  <Switch
                    id="is_featured"
                    checked={formData.is_featured}
                    disabled={featuredCredits <= 0}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })}
                  />
                </div>
                {featuredCredits <= 0 && (
                  <button type="button" onClick={() => navigate('/services')} className="text-xs text-blue-600 hover:underline">Buy a Featured Job Posting credit &rarr;</button>
                )}
                <div className="flex items-center justify-between pt-2 border-t">
                  <div>
                    <Label htmlFor="is_urgent">Mark as urgent hire</Label>
                    <p className="text-xs text-muted-foreground">Highlights the role as urgently hiring. {urgentCredits > 0 ? `${urgentCredits} credit(s) available.` : 'Requires a Job Advert Credit.'}</p>
                  </div>
                  <Switch
                    id="is_urgent"
                    checked={formData.is_urgent}
                    disabled={urgentCredits <= 0}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_urgent: checked })}
                  />
                </div>
                {urgentCredits <= 0 && (
                  <button type="button" onClick={() => navigate('/services')} className="text-xs text-blue-600 hover:underline">Buy a Job Advert Credit &rarr;</button>
                )}
              </div>

              {/* Salary */}
              <div className="space-y-2">
                <Label>Salary Range</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Select value={formData.currency} onValueChange={(value) => setFormData({ ...formData, currency: value })}>
                    <SelectTrigger data-testid="salary-currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(currencies.length > 0 ? currencies : [{ code: 'GBP', symbol: '£', name: 'British Pound' }]).map(c => (
                        <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    name="salary_min"
                    type="number"
                    placeholder="Minimum"
                    value={formData.salary_min}
                    onChange={handleChange}
                    data-testid="salary-min-input"
                  />
                  <Input
                    name="salary_max"
                    type="number"
                    placeholder="Maximum"
                    value={formData.salary_max}
                    onChange={handleChange}
                    data-testid="salary-max-input"
                  />
                  <Select value={formData.salary_period} onValueChange={(value) => setFormData({ ...formData, salary_period: value })}>
                    <SelectTrigger data-testid="salary-period-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="year">Per year</SelectItem>
                      <SelectItem value="month">Per month</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Job Description *</Label>
                <Textarea
                  id="description"
                  name="description"
                  placeholder="Describe the role, responsibilities, and what makes it exciting..."
                  value={formData.description}
                  onChange={handleChange}
                  rows={6}
                  required
                  data-testid="description-input"
                />
              </div>

              {/* Experience */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="experience_required">Experience Required</Label>
                  <Input
                    id="experience_required"
                    name="experience_required"
                    placeholder="e.g. 3-5 years"
                    value={formData.experience_required}
                    onChange={handleChange}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="industry">Industry</Label>
                  <Input
                    id="industry"
                    name="industry"
                    placeholder="e.g. Technology"
                    value={formData.industry}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* Skills */}
              <div className="space-y-2">
                <Label>Required Skills</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add a skill"
                    value={newSkill}
                    onChange={(e) => setNewSkill(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addItem(newSkill, skills, setSkills, setNewSkill))}
                    data-testid="skill-input"
                  />
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => addItem(newSkill, skills, setSkills, setNewSkill)}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {skills.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {skills.map((skill, idx) => (
                      <Badge key={idx} variant="secondary" className="pr-1">
                        {skill}
                        <button 
                          type="button"
                          onClick={() => removeItem(idx, skills, setSkills)}
                          className="ml-1 hover:text-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Requirements */}
              <div className="space-y-2">
                <Label>Requirements</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add a requirement"
                    value={newRequirement}
                    onChange={(e) => setNewRequirement(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addItem(newRequirement, requirements, setRequirements, setNewRequirement))}
                    data-testid="requirement-input"
                  />
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => addItem(newRequirement, requirements, setRequirements, setNewRequirement)}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {requirements.length > 0 && (
                  <ul className="space-y-2 mt-2">
                    {requirements.map((req, idx) => (
                      <li key={idx} className="flex items-center justify-between p-2 bg-secondary rounded-lg text-sm">
                        <span>{req}</span>
                        <button 
                          type="button"
                          onClick={() => removeItem(idx, requirements, setRequirements)}
                          className="text-muted-foreground hover:text-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Benefits */}
              <div className="space-y-2">
                <Label>Benefits</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add a benefit"
                    value={newBenefit}
                    onChange={(e) => setNewBenefit(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addItem(newBenefit, benefits, setBenefits, setNewBenefit))}
                    data-testid="benefit-input"
                  />
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => addItem(newBenefit, benefits, setBenefits, setNewBenefit)}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {benefits.length > 0 && (
                  <ul className="space-y-2 mt-2">
                    {benefits.map((ben, idx) => (
                      <li key={idx} className="flex items-center justify-between p-2 bg-secondary rounded-lg text-sm">
                        <span>{ben}</span>
                        <button 
                          type="button"
                          onClick={() => removeItem(idx, benefits, setBenefits)}
                          className="text-muted-foreground hover:text-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Submit */}
              <div className="flex gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => navigate(-1)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 bg-[#E11D48] hover:bg-[#E11D48]/90"
                  disabled={loading}
                  data-testid="submit-job-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {isEdit ? 'Save Changes' : 'Post Job'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
